/**
 * Shared baggage key to Vanguard attribute mapping
 *
 * Single source of truth for baggage key transformations.
 * Used by BaggageSpanProcessor and other OpenTelemetry components.
 */

export const BAGGAGE_TO_VG_ATTRIBUTE: Record<string, string> = {
  'cl': 'vg.client.poid',     // Client POID
  're': 'vg.referer',          // Referer URL (sanitized)
  'cc': 'vg.call_chain',       // Call chain (UI → ROB → VEX)
  'ch': 'vg.channel',          // Channel (web, mobile)
  'ji': 'vg.journey.id',       // Journey ID (multi-step flows)
  'cp': 'vg.crew.poid',        // Crew POID (advisor actions)
  'ci': 'vg.correlation.id',   // Correlation ID (workflows)
  'ac': 'vg.acid'              // ACID (tenant identifier)
};

/**
 * Maps short baggage key to full Vanguard attribute name
 *
 * @param baggageKey - Short key from baggage (e.g., 'cl', 're', 'cc')
 * @returns Full vg.* attribute name (e.g., 'vg.client.poid')
 *
 * @example
 * mapBaggageKeyToVgAttribute('cl') // Returns: 'vg.client.poid'
 * mapBaggageKeyToVgAttribute('unknown') // Returns: 'vg.unknown'
 */
export function mapBaggageKeyToVgAttribute(baggageKey: string): string {
  return BAGGAGE_TO_VG_ATTRIBUTE[baggageKey] || `vg.${baggageKey}`;
}

/**
 * Sanitizes referer URL to remove sensitive query parameters
 *
 * Strips query string and hash fragment to prevent leaking:
 * - Auth tokens (?token=abc123)
 * - Session IDs (?sessionId=xyz789)
 * - PII (?email=user@example.com)
 *
 * @returns Sanitized URL with only origin + pathname
 *
 * @example
 * // Before: https://app.vanguard.com/page?token=secret&id=123#section
 * // After:  https://app.vanguard.com/page
 */
export function getSanitizedReferer(): string {
  try {
    const url = new URL(window.location.href);
    // Only include origin + pathname (no query/hash)
    return `${url.origin}${url.pathname}`;
  } catch (error) {
    // Fallback to origin if URL parsing fails
    return window.location.origin;
  }
}

/**
 * Sanitizes URL for span names to prevent high cardinality
 *
 * Removes query parameters from URL to keep span names low-cardinality.
 * Query params are still captured in span attributes (http.url).
 *
 * @param fullUrl - Full URL with potential query params
 * @returns Pathname only (e.g., "/api/users" instead of "/api/users?id=123")
 */
export function sanitizeUrlForSpanName(fullUrl: string): string {
  try {
    const url = new URL(fullUrl, window.location.origin);
    return url.pathname;
  } catch (error) {
    // If URL parsing fails, return as-is (better than crashing)
    return fullUrl;
  }
}
